
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { CATEGORIES_QUERY } from 'app/gql';
import { GroupUtils } from '../../../utils/GroupUtils';
import { useUserDetailsQuery } from './useUserDetailsQuery';
import { SEARCH_TOTAL_QUERY } from '../../queries/SEARCH_TOTAL_QUERY';
import { apolloService, authService } from '../../../services';

export function useCategoryListQuery( options ) {
    const {
        data: userDetailsResponse,
        loading: userDetailsLoading
    } = useUserDetailsQuery( { fetchPolicy: 'network-only' } );

    const {
        data: categoriesResponse,
        loading: categoriesLoading
    } = useQuery( CATEGORIES_QUERY, { ...options } );

    const userSegmentData = useMemo( () => {
        if ( userDetailsLoading ) {return [];}

        const userGroups = userDetailsResponse?.person?.groups || [];
        const segments = GroupUtils.getSegmentGroups( userGroups );

        return segments;

    }, [ userDetailsLoading ] );

    const categoryData = useMemo( () => {
        if ( categoriesLoading ) {return [];}

        return categoriesResponse?.categories?.edges ?? [];
    }, [ categoriesLoading ] );

    const [ validCategories, setValidCategories ] = useState( null );

    /**
     * An array of all category IDs being used by courses and curriculums. If configured
     * to not hide unused categories, this will simply be an empty array to reduce
     * unnecessary calculations.
     */
     useEffect( () => {
        if ( !categoryData || userSegmentData?.length == 0 ) {
            return;
        }
        ( async () => {
            const categoryIds = categoryData?.map( c => c.node.id ) ?? [];
    
            const catTotals = await Promise.all( categoryIds.map( async c => {
                const prom = await apolloService.query( {
                    query: SEARCH_TOTAL_QUERY,
                    variables: {
                        types: [ 'courses', 'curricula' ],
                        query: '',
                        page: 1,
                        limit: 0, 
                        sort: 'name',
                        order: 'asc',
                        categoryId: c,
                        personId: authService.isAdminUser() ? undefined : userDetailsResponse?.person.id,
                    },
                    context: { headers: { authorization: `Bearer ${ authService.getAccessToken() }` } },
                    fetchPolicy: 'cache-first',
                } );
                return {
                    id: c,
                    totalProm: prom
                };
            } ) );
    
            const userBrands = userSegmentData.find( s => s.name == 'brand' )?.ids;
    
            const validCats = categoryData
                .filter( c => catTotals.find( ct => ct.id == c.node.id ).totalProm.data.search.totalCount > 0 )
                .filter( e => userBrands?.includes( e.node.brand.segmentId ) );
    
            setValidCategories( validCats );
    
        } )();
     }, [ categoryData, userSegmentData ] );

    return useMemo ( () => ( {
        data: validCategories,
        loading: validCategories == null
    } ), [ validCategories ] );
}
